import { mainIconSize } from '@/constants';
import { useStudySamplesCsvDownload } from '@/hooks';
import { Study } from '@/types';
import { type ButtonProps, getStackClassName } from '@nautilusbio/nb-design-system';
import { Breadcrumbs } from '@nautilusbio/nb-design-system/Breadcrumbs';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Dialog } from '@nautilusbio/nb-design-system/Dialog';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text, TextProps } from '@nautilusbio/nb-design-system/Text';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { TFunction } from 'i18next';
import { Download, PencilIcon } from 'lucide-react';
import React, { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EditStudyForm } from './EditStudyForm';

interface StudyTitleBarProps {
  studyStyles: { [key: string]: string };
  pageStyles: { [key: string]: string };
  t: TFunction<'studies', undefined>;
  data?: Study;
  orgId?: string;
  canEdit?: boolean;
  detailTitle?: string;
  /** For routes whose sample table already renders its own sort/filter-aware download button. */
  hideDownloadButton?: boolean;
}

const sharedBtnProps: Partial<ButtonProps> = {
  className: getStackClassName(['displayflex', 'horizontal', 'gap3']),
  size: 'md',
  visual: 'unstyled',
};
const sharedTextProps: Partial<TextProps> = {
  as: 'span',
  noWrap: true,
  size: 'md',
};

export const StudyTitleBar: FC<StudyTitleBarProps> = ({
  canEdit = false,
  data,
  detailTitle,
  hideDownloadButton = false,
  orgId,
  pageStyles,
  studyStyles,
  t,
}) => {
  const { t: tStudy } = useTranslation('studies');
  const [editOpen, setEditOpen] = useState(false);

  const handleEditSuccess = useCallback(() => {
    setEditOpen(false);
  }, []);

  // The study's sample list, which is what this bar's "Download Study" means — the same export the
  // Study Detail page's own samples table offers, which is why that page hides this button rather
  // than rendering both. There is no grid in the title bar, so it carries no sort/filter state.
  const downloadStudySamples = useStudySamplesCsvDownload(orgId ?? '', data?.id ?? '');
  const handleDownloadStudy = useCallback(() => {
    downloadStudySamples.mutate(undefined, {
      onError: () => toast.error(tStudy('study.studySamplesDownloadFailed')),
    });
  }, [downloadStudySamples, tStudy]);

  return (
    <Stack
      className={clsx(pageStyles.titleRow, pageStyles.organizationTitle, studyStyles.studyTitleBar)}
      fullWidth
      justifyContent="space-between"
      orientation="horizontal"
    >
      <Breadcrumbs
        capitalize
        links={[
          { id: 'studies', children: t('study.home'), to: '/studies' },
          {
            id: 'study',
            children: data?.name ?? '',
            ...(detailTitle ? { to: '/studies/$studyId', params: { studyId: data?.id ?? '' } } : {}),
          },
          ...(detailTitle ? [{ id: 'detail', children: detailTitle }] : []),
        ]}
        renderLink={(link, _index, defaultLinkProps) => {
          if (!link?.to) return link?.children;

          return (
            <Link className={defaultLinkProps.className} to={link.to}>
              {link.children}
            </Link>
          );
        }}
        separator="arrow"
        size="md"
      />

      <Stack alignItems="center" gap={4} orientation="horizontal">
        <Dialog
          content={data && orgId ? <EditStudyForm orgId={orgId} study={data} onSuccess={handleEditSuccess} /> : null}
          data-testid="edit-study-btn"
          floatingProps={{ open: editOpen, onOpenChange: setEditOpen }}
          onOpenChange={setEditOpen}
          title={t('study.edit_study_title')}
          triggerProps={{
            children: (
              <>
                <PencilIcon size={mainIconSize} />
                <Text {...sharedTextProps}>{t('study.edit')}</Text>
              </>
            ),
            isDisabled: !canEdit,
            ...sharedBtnProps,
          }}
        />

        {!hideDownloadButton && (
          <Button
            data-testid="download-study-btn"
            isDisabled={!orgId || !data?.id}
            isPending={downloadStudySamples.isPending}
            onPress={handleDownloadStudy}
            {...sharedBtnProps}
          >
            <Download size={mainIconSize} />
            <Text {...sharedTextProps}>{t('study.download')}</Text>
          </Button>
        )}
      </Stack>
    </Stack>
  );
};
